import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatMXN, formatDate, statusColor, LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, Printer, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cot, setCot] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/cotizaciones/${id}`);
        setCot(data);
      } catch {
        toast.error("No se encontró la cotización");
        navigate("/cotizaciones");
      }
    })();
  }, [id, navigate]);

  if (!cot) return <div className="text-muted-foreground">Cargando…</div>;

  const remove = async () => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    await api.delete(`/cotizaciones/${id}`);
    toast.success("Eliminada");
    navigate("/cotizaciones");
  };

  const duplicar = async () => {
    try {
      const { data } = await api.post(`/cotizaciones/${id}/duplicar`);
      toast.success(`Duplicada como ${data.folio}`);
      navigate(`/cotizaciones/${data.id}/editar`);
    } catch {
      toast.error("Error al duplicar");
    }
  };

  const pickLineImages = (l) => {
    if (l.imagenes && l.imagenes.length) return l.imagenes;
    if (l.imagen_url) return [l.imagen_url];
    return [];
  };
  const allImages = (cot.lineas || []).flatMap((l) =>
    pickLineImages(l).map((src) => ({ src, codigo: l.codigo, descripcion: l.descripcion }))
  );

  return (
    <div className="space-y-6" data-testid="quote-detail-page">
      <header className="flex flex-wrap items-center justify-between gap-3 no-print">
        <Link to="/cotizaciones" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2" data-testid="detail-back">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="detail-print"><Printer className="w-4 h-4 mr-2" /> Imprimir / PDF</Button>
          <Button variant="outline" onClick={duplicar} data-testid="detail-duplicate"><Copy className="w-4 h-4 mr-2" /> Duplicar</Button>
          <Link to={`/cotizaciones/${id}/editar`}><Button variant="outline" data-testid="detail-edit"><Pencil className="w-4 h-4 mr-2" /> Editar</Button></Link>
          <Button variant="outline" onClick={remove} data-testid="detail-delete" className="text-[#BC4749] border-[#BC4749]/40 hover:bg-[#BC4749]/10 hover:text-[#BC4749]"><Trash2 className="w-4 h-4 mr-2" /> Eliminar</Button>
        </div>
      </header>

      <div className="bg-[#F4F2EC]/40 py-6 px-4 rounded-lg print-area">
        <div className="paper-sheet max-w-[820px] mx-auto rounded overflow-hidden">
          {/* Logo as a banner using its own background, no added container */}
          <div className="w-full" data-testid="detail-banner">
            <img src={LOGO_URL} alt="Madera Blanca Studio" className="w-full h-auto block" />
          </div>

          <div className="p-8 md:p-10">
            {/* Info block (compact, sin líneas divisorias) */}
            <table className="text-[10.5px] border-collapse" data-testid="detail-info-block">
              <tbody>
                <tr>
                  <td className="py-0.5 pr-4 font-semibold uppercase tracking-wide w-24 align-top">AT'N:</td>
                  <td className="py-0.5 font-semibold uppercase align-top" data-testid="detail-cliente">{cot.cliente_nombre}</td>
                </tr>
                <tr>
                  <td className="py-0.5 pr-4 font-semibold uppercase tracking-wide align-top">OBRA:</td>
                  <td className="py-0.5 font-semibold uppercase align-top" data-testid="detail-proyecto">{cot.proyecto}</td>
                </tr>
                <tr>
                  <td className="py-0.5 pr-4 font-semibold uppercase tracking-wide align-top">DIRECCIÓN:</td>
                  <td className="py-0.5 font-semibold uppercase align-top" data-testid="detail-ubicacion">{cot.ubicacion || "—"}</td>
                </tr>
                <tr>
                  <td className="py-0.5 pr-4 font-semibold uppercase tracking-wide align-top">FECHA:</td>
                  <td className="py-0.5 font-semibold uppercase align-top">{formatDate(cot.created_at)}</td>
                </tr>
                <tr>
                  <td className="py-0.5 pr-4 font-semibold uppercase tracking-wide align-top">FOLIO:</td>
                  <td className="py-0.5 font-semibold tabular align-top">
                    <span>{cot.folio}</span>
                    <span className={`ml-3 text-[9px] px-2 py-0.5 rounded border ${statusColor(cot.status)} no-print align-middle`}>{cot.status}</span>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Table (solo cuerpo, totales ya no en tfoot para no repetirse por página) */}
            <div className="mt-6 border border-border rounded overflow-hidden">
              <div className="bg-[#bbbbb4] text-[#2C2A28] px-3 py-2 text-[11px] font-bold uppercase tracking-wide">PRESUPUESTO MATERIAL Y MANO DE OBRA</div>
              <table className="w-full text-[10.5px] leading-snug">
                <thead className="bg-[#F4F2EC]">
                  <tr>
                    <th className="px-2 py-2 text-center uppercase tracking-wide font-bold w-16 border-r border-border">CODIGO</th>
                    <th className="px-2 py-2 text-center uppercase tracking-wide font-bold border-r border-border">DESCRIPCIÓN</th>
                    <th className="px-2 py-2 text-center uppercase tracking-wide font-bold w-12 border-r border-border">UNID.</th>
                    <th className="px-2 py-2 text-center uppercase tracking-wide font-bold w-12 border-r border-border">CANT.</th>
                    <th className="px-2 py-2 text-center uppercase tracking-wide font-bold w-20 border-r border-border">P.U.</th>
                    <th className="px-2 py-2 text-center uppercase tracking-wide font-bold w-20">IMPORTE</th>
                  </tr>
                </thead>
                <tbody>
                  {cot.lineas.map((l, idx) => (
                    <tr key={idx} className="border-t border-border align-top" data-testid={`detail-line-${idx}`}>
                      <td className="px-2 py-2 text-center tabular border-r border-border align-middle">{l.codigo}</td>
                      <td className="px-2 py-2 leading-snug border-r border-border font-medium">{l.descripcion}</td>
                      <td className="px-2 py-2 text-center border-r border-border align-middle">{l.unidad}</td>
                      <td className="px-2 py-2 text-center tabular border-r border-border align-middle">{l.cantidad}</td>
                      <td className="px-2 py-2 text-right tabular border-r border-border align-middle">{formatMXN(l.precio_unitario)}</td>
                      <td className="px-2 py-2 text-right tabular align-middle">{formatMXN(Number(l.cantidad) * Number(l.precio_unitario))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totales fuera de la tabla (aparecen solo una vez, donde termina la tabla) */}
            <div className="totals-block mt-3 border border-border rounded overflow-hidden">
              <table className="w-full text-[11px]">
                <tbody>
                  <tr>
                    <td className="px-2 py-1.5 text-right uppercase font-bold bg-[#bbbbb4] w-[calc(100%-8rem)]">SUBTOTAL</td>
                    <td className="px-2 py-1.5 text-right tabular bg-[#bbbbb4] font-bold w-32" data-testid="detail-subtotal">{formatMXN(cot.subtotal)}</td>
                  </tr>
                  {cot.descuento_mostrar && Number(cot.descuento_monto) > 0 && (
                    <tr>
                      <td className="px-2 py-1.5 text-right uppercase font-bold bg-[#e6d5b8] text-[#BC4749]">DESCUENTO</td>
                      <td className="px-2 py-1.5 text-right tabular bg-[#e6d5b8] font-bold text-[#BC4749]" data-testid="detail-descuento">- {formatMXN(cot.descuento_monto)}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="px-2 py-1.5 text-right uppercase font-bold bg-[#999991]">IVA</td>
                    <td className="px-2 py-1.5 text-right tabular bg-[#999991] font-bold" data-testid="detail-iva">{formatMXN(cot.iva)}</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-2 text-right uppercase font-bold bg-[#2C2A28] text-white">TOTAL</td>
                    <td className="px-2 py-2 text-right tabular bg-[#2C2A28] text-white font-bold text-[13px]" data-testid="detail-total">{formatMXN(cot.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Reference images grid */}
            {allImages.length > 0 && (
              <div className="mt-7 images-grid">
                <div className="text-[11px] font-bold uppercase tracking-wide mb-2 underline">IMAGEN DE REFERENCIA:</div>
                <div className="flex flex-wrap gap-2">
                  {allImages.map((img, idx) => (
                    <figure key={idx} className="border border-border rounded overflow-hidden max-w-full m-0" data-testid={`detail-img-${idx}`}>
                      <img src={img.src} alt={img.codigo} className="h-44 w-auto max-w-full object-contain block" />
                      {img.codigo && (
                        <figcaption className="text-[10px] font-semibold uppercase tracking-wide text-center py-1 border-t border-border bg-[#FAFAF7]" data-testid={`detail-img-caption-${idx}`}>{img.codigo}</figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              </div>
            )}

            {/* Bloque final (Notas + Garantía + Condiciones + Bancarios) en una sola hoja */}
            <div className="final-page mt-8 pt-6 border-t border-border">
              {/* Notas */}
              {cot.notas_items && cot.notas_items.length > 0 && (
                <div data-testid="detail-notas">
                  <div className="text-[11px] font-bold uppercase tracking-wide mb-2 underline">Notas</div>
                  <ul className="text-[10.5px] space-y-1 leading-snug">
                    {cot.notas_items.map((n, i) => (<li key={i} className="flex gap-2"><span className="text-[#8B5A33]">*</span><span>{n}</span></li>))}
                  </ul>
                </div>
              )}

              {/* Garantía */}
              {cot.garantia_items && cot.garantia_items.length > 0 && (
                <div className="mt-5" data-testid="detail-garantia">
                  <div className="text-[11px] font-bold uppercase tracking-wide mb-2 underline">Garantía</div>
                  <ul className="text-[10.5px] space-y-1 leading-snug">
                    {cot.garantia_items.map((g, i) => (<li key={i} className="flex gap-2"><span className="text-[#8B5A33]">*</span><span>{g}</span></li>))}
                  </ul>
                </div>
              )}

              {/* Condiciones de pago */}
              {cot.condiciones_pago && (
                <div className="mt-5" data-testid="detail-condiciones">
                  <div className="text-[11px] font-bold uppercase tracking-wide mb-2 underline">Condiciones de pago</div>
                  <p className="text-[10.5px]">{cot.condiciones_pago}</p>
                </div>
              )}

              {/* Datos bancarios */}
              {cot.datos_bancarios && (cot.datos_bancarios.titular || cot.datos_bancarios.banco) && (
                <div className="mt-5" data-testid="detail-bancarios">
                  <div className="text-[11px] font-bold uppercase tracking-wide mb-2 underline">Datos para transferencia o depósito</div>
                  <div className="text-[10.5px] space-y-0.5">
                    {cot.datos_bancarios.titular && <div>{cot.datos_bancarios.titular}</div>}
                    {cot.datos_bancarios.banco && <div>BANCO {cot.datos_bancarios.banco}</div>}
                    {cot.datos_bancarios.cuenta && <div>NÚMERO DE CUENTA: {cot.datos_bancarios.cuenta}</div>}
                    {cot.datos_bancarios.clabe && <div>CLABE INTERBANCARIA: {cot.datos_bancarios.clabe}</div>}
                  </div>
                </div>
              )}

              <div className="mt-8 pt-5 border-t border-border text-[10px] text-muted-foreground flex justify-between uppercase tracking-wide">
                <span>Madera Blanca Studio · Vigencia {cot.vigencia_dias || 15} días</span>
                <span>Atentamente</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
